import React from "react";
import {
  ColumnDef,
  flexRender,
  Table,
  Row,
  RowData,
} from "@tanstack/react-table";
import { cn } from "@/lib/utils";
import { Loader } from "../loader";
import { ScrollArea, ScrollAreaProps } from "@/components/ui/scroll-area";
import { useNoResultsMessage } from "./data-table-no-results";

export type RowContext<TData extends RowData> = {
  row: Row<TData>;
  table: Table<TData>;
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

export type RowDef<TData extends RowData> = React.FC<RowContext<TData>>;

export interface DataTableColumnsProps<TData, TValue>
  extends React.HTMLAttributes<HTMLDivElement> {
  table: Table<TData>;
  columns: ColumnDef<TData, TValue>[];
  box?: RowDef<TData>;
  fill?: boolean;
  loading?: boolean;
  scroll?: ScrollAreaProps;
  currentRef?: React.MutableRefObject<HTMLDivElement | null>;
  noResultsMessage?: string;
}

export const DefaultBox = <TData,>({
  row,
  table,
  children,
  ...props
}: RowContext<TData>) => {
  const cells = React.useMemo(
    () =>
      row
        .getVisibleCells()
        .filter((cell) => !cell.column.columnDef.meta?.box?.hidden),
    [row],
  );
  return (
    <div
      {...props}
      className={cn("flex flex-col items-center", props.className)}
    >
      {cells.map((cell, i) => (
        <React.Fragment key={cell.id ?? i}>
          {flexRender(cell.column?.columnDef?.cell, {
            ...cell.getContext(),
            ...(cell.column.columnDef.meta?.box?.props as any),
          })}
        </React.Fragment>
      ))}
    </div>
  );
};

function Box_<TData>({
  box,
  row,
  table,
  onClick,
  onTouchStart,
  className,
  isSelected,
  isPosition,
  allSelected: _allSelected,
  currentRef,
  ...props
}: {
  box?: RowDef<TData>;
  row: Row<TData>;
  table: Table<TData>;

  isSelected: boolean;
  isPosition: boolean;
  allSelected: boolean;
  currentRef?: React.MutableRefObject<HTMLDivElement | null>;
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>) {
  const chain =
    <T,>(cb?: (ev: T) => any) =>
    (ev: T) => {
      if (
        row.isPreviewEnabled() &&
        (!row.getIsPosition() || (table.getState().preview ?? 0) === 0)
      ) {
        row.setIsPosition(true);
        table.setPreview((u) =>
          u === 0 ? (table.isSkipPreview() ? 2 : 1) : u,
        );
        return;
      }
      if (cb) {
        cb(ev);
      }
    };
  return flexRender(box ?? DefaultBox, {
    row,
    table,
    ...props,
    onClick: chain(onClick),
    className: cn(
      "border rounded-[calc(var(--radius)-1px)] border-border",
      // "cursor-pointer",
      // row.isPreviewEnabled() && !row.getIsPosition() && "cursor-pointer",
      className,
    ),
    ref: (el) => {
      if (row.getIsPosition()) {
        if (currentRef) {
          currentRef.current = el;
        }
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    ...{
      ["data-component"]: "data-table-box",
      ["data-is-selected"]: isSelected,
      ["data-is-position"]: isPosition,
    },
  });
}

export const Box = React.memo(
  Box_,
  (a, b) =>
    a.row.id === b.row.id &&
    a.row.original === b.row.original &&
    a.isSelected === b.isSelected &&
    a.isPosition === b.isPosition &&
    a.allSelected === b.allSelected,
) as typeof Box_;

export default function DataTableBox<TData, TValue = any>({
  table,
  columns,
  box,
  fill = true,
  loading,
  scroll,
  currentRef,
  noResultsMessage = "No results found.",
  ...props
}: DataTableColumnsProps<TData, TValue>) {
  const emptyMessage = useNoResultsMessage(noResultsMessage);
  /**
   * Instead of calling `column.getSize()` on every render for every header
   * and especially every data cell (very expensive),
   * we will calculate all column sizes at once at the root table level in a useMemo
   * and pass the column sizes down as CSS variables to the <table> element.
   */
  const Wrap = React.useMemo(
    () =>
      scroll
        ? ({ children }: { children?: React.ReactNode }) => (
            <ScrollArea
              {...scroll}
              className={cn("relative w-full", scroll?.className)}
            >
              {children}
            </ScrollArea>
          )
        : ({ children }: { children?: React.ReactNode }) => (
            <div className="relative w-full">{children}</div>
          ),
    [],
  );
  return (
    <Wrap>
      {!!loading && (
        <Loader
          data-component="data-table-loading"
          className="absolute left-[calc(50%-1.25rem)] mt-5"
          size="lg"
        />
      )}
      <div
        data-component="data-table-boxes"
        {...props}
        className={cn(
          fill && "w-full flex flex-wrap gap-2",
          loading && "blur-[1px] grayscale",
          props.className,
        )}
      >
        {table.getRowModel().rows?.length ? (
          table
            .getRowModel()
            .rows.map((row) => (
              <Box
                key={row.id}
                box={box}
                table={table}
                row={row}
                isSelected={row.getIsSelected()}
                isPosition={row.getIsPosition()}
                allSelected={table.getAllSelected()}
                currentRef={currentRef}
              />
            ))
        ) : (
          <div
            data-component="data-table-no-results"
            className="w-full flex items-center hover:bg-muted/50 h-24"
          >
            <div className="mx-auto">{emptyMessage}</div>
          </div>
        )}
      </div>
    </Wrap>
  );
}
